import { useContext } from 'react';
import Head from 'next/head';

import { ConfigContext } from '@providers/ConfigProvider';
import { ProductContext } from '@providers/ProductProvider';
import { useIntl } from 'utils/intl';
import getNameByType from 'utils/getNameByType';
import getVideoStructuredData from 'utils/getVideoStructuredDate';
import getStructuredData from 'utils/getStructuredData';
import resolveImageUrl from 'utils/resolveImageUrl';

import { getProductStructuredData } from './ProductHead.utils';
import messages from './ProductHead.messages';

const ProductHead = () => {
  const intl = useIntl();
  const { appUrl, shopId, contact } = useContext(ConfigContext);
  const {
    id,
    product,
    manufacturer,
    productType,
    producerPn,
    productCode,
    ean,
    availability,
    grossPrice,
    thumbnail,
    video,
    names,
    pictures,
    attributes,
    urlSlug,
    rating,
    ratingCount,
    shipsUntil,
    isForSale,
    isPrerelease,
    isEol,
  } = useContext(ProductContext);

  const thumbnailUrl = thumbnail
    ? resolveImageUrl(
        {
          preset: 'og',
          name: thumbnail.basename,
          extension: thumbnail.extension,
          productName: product.name,
        },
        { appUrl }
      )
    : undefined;

  return (
    <Head>
      <title>{getNameByType(names, 'PAGE_TITLE', shopId)}</title>
      <meta name="robots" content={!isForSale ? 'noindex, nofollow' : 'index, follow'} key="robots" />
      <meta property="og:url" content={`${appUrl}/product/${urlSlug}/${id}`} key="og-url" />
      <meta property="og:type" content="product" key="og-type" />
      <meta property="og:title" content={getNameByType(names, 'PAGE_TITLE', shopId)} key="og-title" />
      <meta property="og:description" content={getNameByType(names, 'META_DESCRIPTION', shopId)} key="og-description" />
      {thumbnailUrl && (
        <>
          <meta property="og:image" content={thumbnailUrl} key="og-image" />
          <meta property="og:image:secure_url" content={thumbnailUrl} key="og-image-secure-url" />
          <meta property="og:image:type" content="image/png" key="og-image-type" />
          <meta
            property="og:image:alt"
            content={`${productType.name} - ${product.name} - ${intl.formatMessage(messages.mainPhoto)}`}
            key="og-image-alt"
          />
        </>
      )}
      <meta property="product:price:amount" content={`${grossPrice.amount / 100}`} key="product-price-amount" />
      <meta property="product:price:currency" content={grossPrice.currency} key="product-price-currency" />
      <meta name="description" content={getNameByType(names, 'META_DESCRIPTION', shopId)} key="description" />
      <link rel="canonical" href={`${appUrl}/product/${urlSlug}/${id}`} key="canonical" />
      {getStructuredData(
        getProductStructuredData(
          {
            id,
            names,
            urlSlug,
            product,
            productType,
            manufacturer,
            grossPrice,
            isEol,
            isPrerelease,
            availability,
            producerPn,
            productCode,
            ean,
            rating,
            ratingCount,
            pictures,
            attributes,
            shipsUntil,
          },
          appUrl,
          contact,
          shopId
        )
      )}
      {video && getStructuredData(getVideoStructuredData(video))}
    </Head>
  );
};

export default ProductHead;
